import React, { useState, useEffect } from 'react';
import { 
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer,
  PieChart, Pie, Cell, LineChart, Line, AreaChart, Area
} from 'recharts';
import { 
  TrendingUp, Users, Eye, Monitor, Smartphone, Tablet, 
  Globe, Calendar, Clock, RefreshCw, FileText, Trash2, Shield, AlertTriangle
} from 'lucide-react';
import { Button } from './ui/button';
import { toast } from 'sonner';

const COLORS = ['#14b8a6', '#0891b2', '#6366f1', '#f59e0b', '#ef4444', '#8b5cf6', '#ec4899', '#10b981'];

const AnalyticsDashboard = ({ isSuperAdmin }) => {
  const [analytics, setAnalytics] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [isClearing, setIsClearing] = useState(false);

  const API_URL = process.env.REACT_APP_BACKEND_URL;

  const getAuthHeader = () => {
    const auth = localStorage.getItem('adminAuth');
    return auth ? { 'Authorization': `Basic ${auth}` } : {};
  };

  const fetchAnalytics = async () => {
    setLoading(true);
    try {
      const response = await fetch(`${API_URL}/api/analytics/summary`, {
        headers: getAuthHeader()
      });
      
      if (!response.ok) throw new Error('Failed to fetch analytics');
      
      const data = await response.json();
      setAnalytics(data);
      setError(null);
    } catch (err) {
      setError('Failed to load analytics data');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const handleCleanSpam = async () => {
    if (!window.confirm('This will remove all spam and invalid page entries from analytics. Continue?')) return;
    
    setIsClearing(true);
    try {
      const response = await fetch(`${API_URL}/api/analytics/clean-spam`, {
        method: 'DELETE',
        headers: getAuthHeader()
      });
      
      if (!response.ok) throw new Error('Failed to clean spam');
      
      const data = await response.json();
      toast.success(`Removed ${data.total_deleted} spam entries`);
      fetchAnalytics();
    } catch (err) {
      toast.error('Failed to clean spam data');
      console.error(err);
    } finally {
      setIsClearing(false);
    }
  };

  const handleClearAll = async () => {
    if (!window.confirm('⚠️ WARNING: This will DELETE ALL analytics data permanently. This cannot be undone. Are you sure?')) return;
    if (!window.confirm('Please confirm again - ALL analytics data will be permanently deleted.')) return;
    
    setIsClearing(true);
    try {
      const response = await fetch(`${API_URL}/api/analytics/clear`, {
        method: 'DELETE',
        headers: getAuthHeader()
      });
      
      if (!response.ok) throw new Error('Failed to clear analytics');
      
      const data = await response.json();
      toast.success(`Cleared ${data.deleted_count} records. Analytics reset.`);
      fetchAnalytics();
    } catch (err) {
      toast.error('Failed to clear analytics data');
      console.error(err);
    } finally {
      setIsClearing(false);
    }
  };

  useEffect(() => {
    fetchAnalytics();
  }, []);

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <RefreshCw className="w-8 h-8 text-teal-500 animate-spin" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="text-center py-12">
        <p className="text-red-500 mb-4">{error}</p>
        <Button onClick={fetchAnalytics} variant="outline">
          <RefreshCw className="w-4 h-4 mr-2" />
          Retry
        </Button>
      </div>
    );
  }

  if (!analytics) return null;

  // Format page names for display
  const formatPageName = (path) => {
    if (path === '/') return 'Home';
    return path.replace(/^\//, '').replace(/-/g, ' ').replace(/\//g, ' > ');
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between flex-wrap gap-4">
        <div>
          <h2 className="text-2xl font-bold text-slate-800">Site Analytics</h2>
          <p className="text-sm text-slate-500">Track visitor engagement and page performance</p>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          {isSuperAdmin && (
            <>
              <Button 
                onClick={handleCleanSpam} 
                variant="outline" 
                size="sm"
                disabled={isClearing}
                className="text-amber-600 border-amber-200 hover:bg-amber-50"
              >
                <Shield className="w-4 h-4 mr-2" />
                {isClearing ? 'Cleaning...' : 'Clean Spam'}
              </Button>
              <Button 
                onClick={handleClearAll} 
                variant="outline" 
                size="sm"
                disabled={isClearing}
                className="text-red-600 border-red-200 hover:bg-red-50"
              >
                <Trash2 className="w-4 h-4 mr-2" />
                Clear All Data
              </Button>
            </>
          )}
          <Button onClick={fetchAnalytics} variant="outline" size="sm">
            <RefreshCw className="w-4 h-4 mr-2" />
            Refresh
          </Button>
        </div>
      </div>

      {/* Spam Warning Banner - only show to super admin */}
      {isSuperAdmin && analytics && analytics.page_visits.some(p => 
        ['casino', 'slot', 'poker', 'gambling', 'pharaoh', 'fortune'].some(kw => 
          p.page.toLowerCase().includes(kw)
        )
      ) && (
        <div className="bg-amber-50 border border-amber-200 rounded-xl p-4 flex items-start gap-3">
          <AlertTriangle className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" />
          <div>
            <p className="font-medium text-amber-800">Spam Detected in Analytics</p>
            <p className="text-sm text-amber-700 mt-1">
              We've detected spam entries in your analytics data. Click "Clean Spam" to remove them automatically, 
              or "Clear All Data" to start fresh.
            </p>
          </div>
        </div>
      )}

      {/* Summary Stats */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
        <div className="bg-gradient-to-br from-teal-50 to-teal-100 rounded-xl p-5 border border-teal-200">
          <div className="flex items-center gap-2 mb-2">
            <Eye className="w-5 h-5 text-teal-600" />
            <span className="text-sm font-medium text-teal-700">Total Views</span>
          </div>
          <p className="text-3xl font-bold text-teal-800">{analytics.total_visits.toLocaleString()}</p>
        </div>
        <div className="bg-gradient-to-br from-blue-50 to-blue-100 rounded-xl p-5 border border-blue-200">
          <div className="flex items-center gap-2 mb-2">
            <Users className="w-5 h-5 text-blue-600" />
            <span className="text-sm font-medium text-blue-700">Unique Visitors</span>
          </div>
          <p className="text-3xl font-bold text-blue-800">{analytics.unique_visitors.toLocaleString()}</p>
        </div>
        <div className="bg-gradient-to-br from-emerald-50 to-emerald-100 rounded-xl p-5 border border-emerald-200">
          <div className="flex items-center gap-2 mb-2">
            <Calendar className="w-5 h-5 text-emerald-600" />
            <span className="text-sm font-medium text-emerald-700">Today</span>
          </div>
          <p className="text-3xl font-bold text-emerald-800">{analytics.today_visits.toLocaleString()}</p>
        </div>
        <div className="bg-gradient-to-br from-violet-50 to-violet-100 rounded-xl p-5 border border-violet-200">
          <div className="flex items-center gap-2 mb-2">
            <TrendingUp className="w-5 h-5 text-violet-600" />
            <span className="text-sm font-medium text-violet-700">This Week</span>
          </div>
          <p className="text-3xl font-bold text-violet-800">{analytics.week_visits.toLocaleString()}</p>
        </div>
        <div className="bg-gradient-to-br from-amber-50 to-amber-100 rounded-xl p-5 border border-amber-200">
          <div className="flex items-center gap-2 mb-2">
            <Globe className="w-5 h-5 text-amber-600" />
            <span className="text-sm font-medium text-amber-700">This Month</span>
          </div>
          <p className="text-3xl font-bold text-amber-800">{analytics.month_visits.toLocaleString()}</p>
        </div>
      </div>

      {/* Daily Traffic Chart */}
      {analytics.daily_visits.length > 0 && (
        <div className="bg-white rounded-xl p-6 border border-slate-200">
          <h3 className="text-lg font-bold text-slate-800 mb-4 flex items-center gap-2">
            <TrendingUp className="w-5 h-5 text-teal-600" />
            Daily Traffic (Last 30 Days)
          </h3>
          <div className="h-64">
            <ResponsiveContainer width="100%" height="100%">
              <AreaChart data={analytics.daily_visits}>
                <defs>
                  <linearGradient id="colorViews" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor="#14b8a6" stopOpacity={0.3}/>
                    <stop offset="95%" stopColor="#14b8a6" stopOpacity={0}/>
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                <XAxis 
                  dataKey="date" 
                  tick={{ fontSize: 10 }} 
                  tickFormatter={(date) => new Date(date).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
                />
                <YAxis />
                <Tooltip 
                  labelFormatter={(date) => new Date(date).toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric' })}
                  formatter={(value) => [value, 'Page Views']}
                />
                <Area type="monotone" dataKey="count" stroke="#14b8a6" strokeWidth={2} fill="url(#colorViews)" />
              </AreaChart>
            </ResponsiveContainer>
          </div>
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Top Pages */}
        <div className="bg-white rounded-xl p-6 border border-slate-200">
          <h3 className="text-lg font-bold text-slate-800 mb-4 flex items-center gap-2">
            <FileText className="w-5 h-5 text-blue-600" />
            Top Pages
          </h3>
          {analytics.page_visits.length > 0 ? (
            <div className="space-y-3">
              {analytics.page_visits.slice(0, 10).map((page, idx) => (
                <div key={idx} className="flex items-center justify-between">
                  <div className="flex items-center gap-3 flex-1 min-w-0">
                    <span className={`w-6 h-6 rounded-full flex items-center justify-center text-xs font-bold ${
                      idx === 0 ? 'bg-amber-100 text-amber-700' :
                      idx === 1 ? 'bg-slate-200 text-slate-700' :
                      idx === 2 ? 'bg-orange-100 text-orange-700' :
                      'bg-slate-100 text-slate-600'
                    }`}>
                      {idx + 1}
                    </span>
                    <span className="text-sm text-slate-700 truncate capitalize">
                      {formatPageName(page.page)}
                    </span>
                  </div>
                  <div className="flex items-center gap-2">
                    <div className="w-24 h-2 bg-slate-100 rounded-full overflow-hidden">
                      <div 
                        className="h-full bg-teal-500 rounded-full"
                        style={{ width: `${(page.count / analytics.page_visits[0].count) * 100}%` }}
                      />
                    </div>
                    <span className="text-sm font-medium text-slate-600 w-12 text-right">
                      {page.count}
                    </span>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <p className="text-slate-500 text-center py-4">No page visit data yet</p>
          )}
        </div>

        {/* Device Breakdown */}
        <div className="bg-white rounded-xl p-6 border border-slate-200">
          <h3 className="text-lg font-bold text-slate-800 mb-4 flex items-center gap-2">
            <Monitor className="w-5 h-5 text-violet-600" />
            Devices
          </h3>
          {analytics.devices.length > 0 ? (
            <>
              <div className="h-48">
                <ResponsiveContainer width="100%" height="100%">
                  <PieChart>
                    <Pie
                      data={analytics.devices}
                      cx="50%"
                      cy="50%"
                      innerRadius={50}
                      outerRadius={80}
                      dataKey="count"
                      nameKey="type"
                      label={({ type, percent }) => `${type} ${(percent*100).toFixed(0)}%`}
                    >
                      {analytics.devices.map((entry, index) => (
                        <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                      ))}
                    </Pie>
                    <Tooltip />
                  </PieChart>
                </ResponsiveContainer>
              </div>
              <div className="flex justify-center gap-6 mt-4">
                {analytics.devices.map((device, idx) => (
                  <div key={idx} className="flex items-center gap-2">
                    {device.type === 'Desktop' && <Monitor className="w-4 h-4 text-slate-500" />}
                    {device.type === 'Mobile' && <Smartphone className="w-4 h-4 text-slate-500" />}
                    {device.type === 'Tablet' && <Tablet className="w-4 h-4 text-slate-500" />}
                    <span className="text-sm text-slate-600">{device.type}: {device.count}</span>
                  </div>
                ))}
              </div>
            </>
          ) : (
            <p className="text-slate-500 text-center py-4">No device data yet</p>
          )}
        </div>

        {/* Hourly Activity */}
        {analytics.hourly_visits.length > 0 && (
          <div className="bg-white rounded-xl p-6 border border-slate-200">
            <h3 className="text-lg font-bold text-slate-800 mb-4 flex items-center gap-2">
              <Clock className="w-5 h-5 text-emerald-600" />
              Today's Activity by Hour
            </h3>
            <div className="h-48">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={analytics.hourly_visits}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                  <XAxis dataKey="hour" tickFormatter={(h) => `${h}:00`} tick={{ fontSize: 10 }} />
                  <YAxis />
                  <Tooltip labelFormatter={(h) => `${h}:00 - ${h}:59`} />
                  <Bar dataKey="count" fill="#10b981" radius={[4, 4, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </div>
          </div>
        )}

        {/* Traffic Sources */}
        {analytics.referrers.length > 0 && (
          <div className="bg-white rounded-xl p-6 border border-slate-200">
            <h3 className="text-lg font-bold text-slate-800 mb-4 flex items-center gap-2">
              <Globe className="w-5 h-5 text-amber-600" />
              Traffic Sources
            </h3>
            <div className="space-y-3">
              {analytics.referrers.map((ref, idx) => (
                <div key={idx} className="flex items-center justify-between">
                  <span className="text-sm text-slate-700 truncate flex-1">
                    {ref.source || 'Direct'}
                  </span>
                  <span className="text-sm font-medium text-slate-600 ml-4">
                    {ref.count} visits
                  </span>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default AnalyticsDashboard;
